<template>
  <header class="header">
    <MobileApplicationOffer class="md:hidden" v-if="isGuestRoute"/>
    <div v-if="!isLoggedIn || !user_info.data.space_id"
         class="flex items-center justify-center pt-24">
      <a :href="landingLink"><img src="./logo.svg" alt="" class="h-12"></a>
    </div>
    <div class="container" v-else>
      <div class="header__main-nav">
        <router-link :to="{name:'home'}" class="logo">
          <img src="./logo.svg" alt="">
        </router-link>
        <template v-if="isLoggedIn && !user_info.dirty && user_info.data.space_id">
          <router-link :to="{name:'home'}"
                       class="link"
                       :class="{current:isMoneyRoute}">
            {{ $t('nav.money') }}
          </router-link>
          <router-link :to="{name:'investments'}"
                       class="link"
                       :class="{current:isInvestmentsRoute}">
            {{ $t('nav.investments') }}
          </router-link>
        </template>
        <template v-if="!isLoggedIn">
          <router-link :to="{name:'login'}"
                       class="ml-auto link"
                       :class="{current:$route.name === 'login'}">
            {{ $t('nav.login') }}
          </router-link>
          <router-link :to="{name:'register'}"
                       class="link"
                       :class="{current:$route.name === 'register'}">
            {{ $t('nav.register') }}
          </router-link>
          <LangSelector/>
        </template>
        <template v-else>
          <div class="ml-auto flex items-center">
            <HeaderPortfoliosNavigation v-if="displayPortfoliosNavigation"/>
            <HeaderHelper v-if="displayHeaderHelper"/>
            <router-link :to="{name:'profile-settings'}"
                         class="mr-12"
                         v-tooltip="$t('statusWarning')"
                         v-if="!user_info.dirty && user_info.data.status === 0">
              <font-awesome-icon icon="exclamation-triangle"
                                 class="text-warning"/>
            </router-link>
            <DemoLogoutBtn v-if="user_info.data.username === 'demo'"
                           class="mr-12"/>
            <HeaderUserBlock/>
          </div>
        </template>
      </div>
      <div class="header__sub-nav" v-if="isLoggedIn && !user_info.dirty && user_info.data.space_id">
        <MoneyNavigation v-show="isMoneyRoute"/>
        <InvestmentNavigation v-show="isInvestmentsRoute"/>
        <AccountNavigation v-show="isAccountRoute"/>
      </div>
    </div>
  </header>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import LangSelector from '@/components/common/Header/LangSelector.vue';
import MoneyNavigation from "@/components/common/Header/HeaderSubNavigation/MoneyNavigation";
import InvestmentNavigation from "@/components/common/Header/HeaderSubNavigation/InvestmentNavigation";
import AccountNavigation from "@/components/common/Header/HeaderSubNavigation/AccountNavigation";
import {ACCOUNT_ROUTES} from "@/components/common/Header/HeaderSubNavigation/accountRoutes";
import HeaderUserBlock from "@/components/common/Header/HeaderUserBlock/HeaderUserBlock";
import HeaderPortfoliosNavigation
  from "@/components/common/Header/HeaderPortfolioNavigation/HeaderPortfoliosNavigation";
import HeaderHelper from "@/components/common/Header/HeaderHelper/HeaderHelper";
import MobileApplicationOffer from "@/components/common/MobileApplicationOffer";
import DemoLogoutBtn from "@/components/common/Header/DemoLogoutBtn";

export default {
  name: "Header",
  components: {
    DemoLogoutBtn,
    MobileApplicationOffer,
    HeaderHelper,
    HeaderPortfoliosNavigation,
    HeaderUserBlock,
    AccountNavigation,
    InvestmentNavigation,
    MoneyNavigation,
    LangSelector
  },
  props: {
    isGuestRoute: {
      type: Boolean
    }
  },
  computed: {
    ...mapState('i18n', ['lang']),
    ...mapState('cabinet/user', ['user_info']),
    ...mapGetters('auth', ['isLoggedIn']),
    ...mapGetters('cabinet/helper', ['isHelperAvailableOnPage']),
    ...mapState('investments/portfolios', ['portfolios_list', 'current_portfolio']),
    landingLink() {
      const origin = window.location.origin || 'https://moneykeeper.com'
      return this.lang === 'en' ? `${origin}/en` : origin
    },
    isMoneyRoute() {
      return ['home', 'transactions', 'reports', 'budget', 'goals', 'calendar'].includes(this.$route.name);
    },
    isInvestmentsRoute() {
      return ['investments', 'investments-assets', 'investments-deals', 'investments-analytics'].includes(this.$route.name)
    },
    isAccountRoute() {
      return ACCOUNT_ROUTES.includes(this.$route.name);
    },
    displayPortfoliosNavigation() {
      return this.isInvestmentsRoute && !!this.user_info.data.space_id
          && !!this.portfolios_list.data.length && !!this.current_portfolio.data;
    },
    displayHeaderHelper() {
      return this.isHelperAvailableOnPage && !!(this.user_info.data.settings && this.user_info.data.settings.show_help);
    }
  },
  i18n: {
    messages: {
      en: {
        nav: {
          money: 'Money',
          investments: 'Investments',
          login: 'Sing In',
          register: 'Sing Up',
        },
        statusWarning: 'Your account is not verified'
      },
      ru: {
        nav: {
          money: 'Деньги',
          investments: 'Инвестиции',
          login: 'Войти',
          register: 'Регистрация',
        },
        statusWarning: 'Ваш аккаунт не подтверджен'
      }
    }
  }
}
</script>

<style lang="scss">
.header {
  background: url("./header-bg.svg"),
  radial-gradient(58.11% 306.71% at 23.93% 10%, #1F313F 0%, #142736 38.81%);
  background-repeat: no-repeat;
  background-position-y: 28%;
  background-position-x: 119%;
  margin-bottom: -60px;
  padding-bottom: 77px;
  min-height: 190px;

  &__main-nav {
    @apply flex relative items-center text-lg;
    padding: 1.7rem 0;

    &:after {
      content: '';
      position: absolute;
      bottom: -.1rem;
      left: 0;
      height: 2px;
      width: 100%;
      background: #28445C;
      border-radius: 2px;
    }

    .logo {
      margin-right: 84px;
    }

    .link {
      @apply mr-12 relative transition duration-200;
      padding: 0 1rem;
      color: #C7D6DC;
      line-height: 36px;

      &:after {
        @apply transition duration-200;
        content: '';
        position: absolute;
        bottom: 0;
        transform: translateY(1.8rem);
        width: 100%;
        height: 2px;
        border-radius: 2px;
        left: 0;
        background: #E04067;
        z-index: 1;
        opacity: 0;
      }

      &.current,
      &:hover {
        color: white;
      }

      &:hover:after,
      &.current:after {
        opacity: 1;
      }
    }

    .user {
      margin-left: auto;
    }
  }

  &__sub-nav {
    .link {
      @apply text-md transition duration-200 cursor-pointer;
      color: #C7D6DC;
      line-height: 1.6rem;

      &:hover {
        @apply text-white;
      }

      &.router-link-exact-active,
      &.current {
        color: white;
        font-weight: bold;
      }
    }
  }

  @media(max-width: 1199px) {
    background-position-x: 84%;
    margin-bottom: -5rem;
  }
}
</style>
